/**
 * @file unitsAfterDelivery.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-11-01
 * @description A customer order line's UNITS corrected after delivery (Cam,
 *              30 Oct 2026 — SO-D10442: "I need to be able to change the
 *              units on a delivered customer order, which affects the weight
 *              and pallets … it will trickle down the PO-H correcting its
 *              pallets and weight and units, and the PO-D, which will then
 *              match the product and haulages bills that already linked to
 *              this order").
 *
 *   THE QUANTITY STAYS, THE MONEY FOLLOWS THE CORRECTED TONNAGE (Cam, 30
 *   Oct 2026: "if the qty is 2 and units Tonnes, then I change units to
 *   Totes, then that quantity is 2 Totes, meaning 1 Tonne, and so the unit
 *   price (if in Tonnes) would need to be multiplied by 1 instead of the
 *   original 2"). The line's units — its own unit weight (a fixed unit,
 *   Other / Weight, or None = 0), qty per pallet and pallets — change; its
 *   ordered and supplied quantities stay as they are ("2 stays 2 — now 2 ×
 *   500kg tote (2 pallets) · 1.00 T"). A price is always per SOME unit and
 *   only the person knows which, so each line whose unit changes is asked
 *   (MONEY_BASES): the price was per the OLD unit (the default — how the
 *   system read it until now) → it is converted through kg, 4 dp
 *   (utils/itemCostUnit.scaleForUnitChange — £369.00 a tonne = £184.50 a
 *   500kg tote, line £369.00); per the NEW unit → it stays (£369.00 a tote,
 *   line £738.00); or, where either unit is None or unknown and nothing
 *   converts, TYPED. The one answer applies to the sale price, the order
 *   line's cost and the cost of the PO-D line it was bought on. The server
 *   works the figures out again from what is stored (unitsMoneyPlan) and
 *   never takes the screen's.
 *
 *   WHERE IT GOES, in the order save's own transaction (routes/orders.js):
 *   the order line; the PO-D line it was bought on (unit weight, qty per
 *   pallet, pallets; its quantity kept, its cost by the line's answer) even
 *   when that PO is billed — it is our record, and makes the PO line read as
 *   its bill does; its PO-H
 *   line(s) — unit weight, words, weight and pallets — even when the PO-H is
 *   billed (a record then: no revised PO-H is offered). The PO editor takes
 *   the same correction the other way (units sync both ways).
 *
 *   REFUSED, each line named with why (the server's refusal and the grid's
 *   greyed Units / Weight / Pallets cells say the same words):
 *     - the order is INVOICED (a live invoice that is not a draft), in a
 *       state INVOICE_UNIT_HANDLING says 'refuse' — each of the three states
 *       (raised and not gone out, gone out to the customer, paid) has its
 *       own words. Not gone out is 'change': the invoice is offered the same
 *       correction (services/invoiceSupplyService.decideUnits). Gone out is
 *       'change' too (1.1.0), then the customer is offered the corrected
 *       statement and invoices, or the invoice (services/correctedSendService
 *       — correctionNoteText, statementSendBlock). Paid is
 *       'flag' (1.1.0): the order is corrected, the invoice is NOT changed —
 *       it is flagged "Needs correcting" for a person
 *       (services/invoiceCorrectionFlagService; refused until its migration
 *       has run). A delivery draft awaiting pricing is no invoice: it is
 *       rebuilt from the order;
 *     - the line's PO line is a split family's SHARED line (it carries the
 *       family's quantity — its units are not one order's);
 *     - the line's PO line is on a PO MADE IN XERO;
 *     - a split order's tonnage is ADDED to (or from) the line (its added
 *       quantity is in the target line's unit — take it back out first).
 *
 *   The server's utils/unitsAfterDelivery.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { isNoWeight, lineWeightKg } from './lineUnits';
import { quantityWithTonnesText } from './qtyWithTonnes';
import { formatCount } from './numberFormatters';
import { scaleForUnitChange, perUnitWords, costMoney } from './itemCostUnit';

/** The audit `_source` of a units correction after delivery. */
export const UNITS_AFTER_DELIVERY_SOURCE = 'Units corrected after delivery';

/** The three invoiced states a units correction meets. */
export const INVOICE_UNIT_STATES = ['not_gone_out', 'gone_out', 'paid'];

/**
 * How a units correction is handled on an invoiced order, by the invoice's
 * state. 'refuse' = refused, with the state's words. The answer for each is
 * Cam's to give (30 Oct 2026); until then all three refuse.
 */
export const INVOICE_UNIT_HANDLING = { not_gone_out: 'change', gone_out: 'change', paid: 'flag' };

/** The save's code when an invoice the correction would change is behind Xero's lock. */
export const UNITS_LOCK_CODE = 'UNITS_INVOICE_LOCKED';

const ukDay = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v || '');
};

/**
 * "INV-D00030 (dated 19/10/2026) is behind Xero's accounting lock
 * (31/10/2026). Remove the Xero accounting lock first, then return here and
 * press Save again — nothing has been lost." (Cam, 30 Oct 2026.)
 * @param {Array<{ invoice_no?: string, invoice_date?: string }>} invoices
 * @param {string|null} lockDate — 'YYYY-MM-DD'
 */
export function unitsLockMessage(invoices, lockDate) {
  const list = (invoices || []).map(i => `${i.invoice_no || 'The invoice'}${i.invoice_date ? ` (dated ${ukDay(i.invoice_date)})` : ''}`);
  const subject = list.length <= 1 ? (list[0] || 'The invoice') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  return `${subject} ${list.length > 1 ? 'are' : 'is'} behind Xero's accounting lock${lockDate ? ` (${ukDay(lockDate)})` : ''}. `
    + 'Remove the Xero accounting lock first, then return here and press Save again — nothing has been lost.';
}

const UNIT_FIELDS = ['unit_weight_kg', 'qty_per_pallet', 'pallets'];

const blank = (v) => v === '' || v === null || v === undefined;
/** As the order save stores a line's own unit (routes/orders.js unitOrNull). */
const unitOrNull = (v) => {
  if (blank(v)) return null;
  const x = parseFloat(v);
  return Number.isFinite(x) && x >= 0 ? x : null;
};
const pick = (row, snake, camel) => (row ? (row[snake] !== undefined ? row[snake] : row[camel]) : undefined);

/**
 * A line's OWN units as the order save stores them: None (0) has no qty per
 * pallet and 0 pallets; null = the item's. Either naming (snake / camel).
 * @returns {{ unit_weight_kg: number|null, qty_per_pallet: number|null, pallets: number|null }}
 */
export function lineOwnUnits(row) {
  const w = pick(row, 'unit_weight_kg', 'unitWeightKg');
  if (isNoWeight(w)) return { unit_weight_kg: 0, qty_per_pallet: null, pallets: 0 };
  return {
    unit_weight_kg: unitOrNull(w),
    qty_per_pallet: unitOrNull(pick(row, 'qty_per_pallet', 'qtyPerPallet')),
    pallets: unitOrNull(pick(row, 'pallets', 'pallets'))
  };
}

/**
 * What a save changes in a line's own units, field by field
 * ({ unit_weight_kg: { old, new } … }), or null when nothing.
 */
export function unitsChange(prev, next) {
  const a = lineOwnUnits(prev);
  const b = lineOwnUnits(next);
  const out = {};
  for (const f of UNIT_FIELDS) if (a[f] !== b[f]) out[f] = { old: a[f], new: b[f] };
  return Object.keys(out).length ? out : null;
}

const listOf = (names) => {
  const n = [...new Set((names || []).filter(Boolean))];
  if (n.length <= 1) return n[0] || '';
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
};

/**
 * The order's invoiced state for a units correction, from its live invoices
 * (not voided / deleted, not credit notes — services/invoiceGoneOutService
 * .invoicesForOrders, each with `gone_out`). A draft is no invoice (a
 * delivery draft is rebuilt from the order). Paid (anything paid or
 * allocated against it) wins over gone out, gone out over not gone out.
 *
 * @param {Array<{ invoice_no?: string|null, status?: string, amount_paid?: number|string,
 *   gone_out?: { text?: string }|null }>} invoices
 * @returns {null|{ state: 'not_gone_out'|'gone_out'|'paid', invoice_nos: string[], text: string }}
 */
export function invoiceUnitState(invoices) {
  const live = (invoices || []).filter(i => i && String(i.status || '').toUpperCase() !== 'DRAFT');
  if (!live.length) return null;
  const nos = [...new Set(live.map(i => i.invoice_no).filter(Boolean))];
  const named = listOf(nos);
  const paid = live.filter(i => Number(i.amount_paid) > 0 || String(i.status || '').toUpperCase() === 'PAID');
  if (paid.length) {
    const p = listOf(paid.map(i => i.invoice_no)) || named || 'Its invoice';
    return { state: 'paid', invoice_nos: nos, text: `${p} ${paid.length > 1 ? 'are' : 'is'} paid (or has a payment or credit allocated against it)` };
  }
  const gone = live.filter(i => i.gone_out);
  if (gone.length) {
    const texts = gone.map(i => i.gone_out && i.gone_out.text).filter(Boolean);
    return { state: 'gone_out', invoice_nos: nos, text: texts.length ? texts.join('; ') : `${named || 'Its invoice'} has gone out to the customer` };
  }
  return { state: 'not_gone_out', invoice_nos: nos, text: `${named || 'Its invoice'} has been raised (not yet gone out to the customer)` };
}

/**
 * Why an invoiced order's units cannot be corrected, by the invoice's state,
 * or null when that state's handling is not 'refuse' (or there is no invoice).
 */
export function invoiceUnitBlock(invoice, handling = INVOICE_UNIT_HANDLING) {
  if (!invoice || !invoice.state) return null;
  if ((handling || {})[invoice.state] !== 'refuse') return null;
  return `${invoice.text} — the invoice states each quantity per its unit, so the units stay as invoiced; raise a credit or a debit to correct it`;
}

/**
 * Why one line's units cannot be corrected after delivery, or null.
 *
 * @param {{ invoice?: object|null, poNumber?: string|null, poXeroMade?: boolean,
 *   sharedWith?: string[], tonnage?: { source?: string, target?: string }|null }} facts
 */
export function unitCorrectionBlock(facts = {}) {
  const inv = invoiceUnitBlock(facts.invoice, facts.handling || INVOICE_UNIT_HANDLING);
  if (inv) return inv;
  const po = facts.poNumber || 'its PO';
  if (facts.poXeroMade) {
    return `it was bought on ${po}, which was made in Xero — its units are not corrected from the order`;
  }
  const others = (facts.sharedWith || []).filter(Boolean);
  if (others.length) {
    return `${po} carries it for ${listOf(others)} too (a split family's shared PO line) — its units are not changed from one order`;
  }
  if (facts.tonnage) {
    const t = facts.tonnage;
    return `${t.source || 'a split order'}'s tonnage is added to ${t.target || 'another order'} on this line — take it back out first (Added tonnage…)`;
  }
  return null;
}

/**
 * The line's quantity in its units, in words: "2 × 500kg tote (2 pallets) ·
 * 1.00 T", "2.00 T", "1" (None), the bare figure when nobody knows the unit.
 *
 * @param {number|string} quantity
 * @param {object} units — lineOwnUnits shape, or a row in either naming
 * @param {{ itemUnitWeightKg?: number|string|null, itemQtyPerPallet?: number|string|null, accountCode?: string|null }} [item]
 */
export function unitsWords(quantity, units, item = {}) {
  const own = lineOwnUnits(units);
  return quantityWithTonnesText(quantity, {
    unitWeightKg: own.unit_weight_kg,
    itemUnitWeightKg: own.unit_weight_kg === null ? item.itemUnitWeightKg : null,
    qtyPerPallet: own.qty_per_pallet,
    itemQtyPerPallet: own.unit_weight_kg === null ? item.itemQtyPerPallet : null,
    pallets: own.pallets,
    accountCode: item.accountCode || null
  });
}

/**
 * What a units correction means, said on the line (the order screen) and in
 * the save's answer: "2 stays 2 — now 2 × 500kg tote (2 pallets) · 1.00 T
 * (was 2.00 T)." Null when the units do not change. The money is the line's
 * answer (unitsMoneyPlan / unitsMoneyText).
 */
export function unitCorrectionNote({ quantity, before, after, item = {} }) {
  if (!unitsChange(before, after)) return null;
  const q = formatCount(Number(quantity) || 0);
  const now = unitsWords(quantity, after, item);
  const was = unitsWords(quantity, before, item);
  return `${q} stays ${q} — now ${now}${was && was !== now ? ` (was ${was})` : ''}.`;
}

/**
 * What a corrected line's price was per (the question asked on each line):
 * 'old' — the old unit, converted through kg (the default); 'new' — the new
 * unit, kept as it is; 'typed' — typed afresh (where nothing converts).
 */
export const MONEY_BASES = ['old', 'new', 'typed'];

/** The save's code when a line's answer is missing or cannot be taken. */
export const UNITS_MONEY_CODE = 'UNITS_PRICE_BASIS';

const moneyOrNull = (v) => {
  if (blank(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * The money of a line whose units are corrected, worked out from what is
 * STORED and the person's answer — the same on the screen (to show it) and
 * on the server (to write it; it never takes the screen's figures).
 *
 * @param {{ before: object, after: object, itemUnitWeightKg?: number|string|null,
 *   price?: number|string|null, cost?: number|string|null, quantity?: number|string,
 *   basis?: string|null, typedPrice?: number|string|null, typedCost?: number|string|null }} a
 *   before / after — the line's own units (either naming); price / cost — as
 *   stored (cost null = derived, left so); typedPrice / typedCost — 'typed' only
 * @returns {{ convertible: boolean, sameKg: boolean, oldKg: number|null, newKg: number|null,
 *   basis: string|null, price: { old, new }, cost: { old, new }, problem: string|null,
 *   scale: (v) => number|null }}
 */
export function unitsMoneyPlan(a = {}) {
  const oldKg = lineWeightKg(pick(a.before, 'unit_weight_kg', 'unitWeightKg'), a.itemUnitWeightKg);
  const newKg = lineWeightKg(pick(a.after, 'unit_weight_kg', 'unitWeightKg'), a.itemUnitWeightKg);
  const price = moneyOrNull(a.price);
  const cost = moneyOrNull(a.cost);
  const sameKg = oldKg !== null && newKg !== null && oldKg === newKg;
  const convertible = !!(oldKg && newKg);
  const keep = { price: { old: price, new: price }, cost: { old: cost, new: cost } };
  const base = { convertible, sameKg, oldKg, newKg };
  // The same weight a unit (qty per pallet or pallets only): nothing to ask.
  if (sameKg) return { ...base, basis: null, ...keep, problem: null, scale: (v) => moneyOrNull(v) };
  const basis = MONEY_BASES.includes(a.basis) ? a.basis : (convertible ? 'old' : null);
  if (basis === 'old') {
    if (!convertible) {
      return { ...base, basis, ...keep, scale: (v) => moneyOrNull(v),
        problem: `${unitPhrase(oldKg)} → ${unitPhrase(newKg)} can't be converted — type the price per the new unit` };
    }
    const scale = (v) => {
      const n = moneyOrNull(v);
      if (n === null) return null;
      const s = scaleForUnitChange(n, oldKg, newKg);
      return s === null ? n : s;
    };
    return { ...base, basis, price: { old: price, new: scale(price) }, cost: { old: cost, new: scale(cost) }, problem: null, scale };
  }
  if (basis === 'new') return { ...base, basis, ...keep, problem: null, scale: (v) => moneyOrNull(v) };
  if (basis === 'typed') {
    const tp = moneyOrNull(a.typedPrice);
    const tc = a.typedCost === undefined ? undefined : moneyOrNull(a.typedCost);
    const problem = tp === null || tp < 0 ? 'type the price per the new unit (0 or more)'
      : (tc !== undefined && tc !== null && tc < 0 ? 'a cost below 0 can\'t be taken' : null);
    // No cost typed (blank / not sent): the cost stays as it is.
    const newCost = tc === undefined || tc === null ? cost : tc;
    return { ...base, basis, price: { old: price, new: problem ? price : tp }, cost: { old: cost, new: problem ? cost : newCost },
      problem, scale: (v) => moneyOrNull(v), typedCost: tc };
  }
  return { ...base, basis: null, ...keep, scale: (v) => moneyOrNull(v),
    problem: `${unitPhrase(oldKg)} → ${unitPhrase(newKg)} can't be converted — type the price per the new unit` };
}

/** "a tonne", "a 500kg tote", "no weight", "an unknown unit". */
export function unitPhrase(kg) {
  if (kg === 0) return 'no weight (None)';
  if (!kg) return 'an unknown unit';
  return `a ${perUnitWords(kg)}`;
}

/**
 * The choices the order screen offers on a corrected line, with what each
 * makes of the price and the line: "£369.00 on this line was per a tonne →
 * £184.50 a 500kg tote, line £369.00" / "… a 500kg tote → stays £369.00,
 * line £738.00". Typed when nothing converts.
 */
export function unitsMoneyChoices({ before, after, itemUnitWeightKg, price, quantity } = {}) {
  const q = Number(quantity) || 0;
  const plan = unitsMoneyPlan({ before, after, itemUnitWeightKg, price, basis: 'old' });
  if (plan.sameKg) return [];
  const p = moneyOrNull(price);
  const money = (v) => (v === null ? '' : costMoney(v));
  const line = (v) => (v === null ? '' : `line ${costMoney(round2(q * v))}`);
  if (!plan.convertible) {
    return [{ basis: 'typed', label: plan.newKg === 0 ? 'Type the price each (it has no weight now)' : `Type the price per ${plan.newKg ? unitPhrase(plan.newKg) : 'the new unit'}`,
      detail: `${unitPhrase(plan.oldKg)} → ${unitPhrase(plan.newKg)} can't be converted` }];
  }
  return [
    { basis: 'old', label: `It was per ${unitPhrase(plan.oldKg)}`,
      detail: p === null ? '' : `${money(plan.price.new)} ${unitPhrase(plan.newKg)}, ${line(plan.price.new)}` },
    { basis: 'new', label: `It was per ${unitPhrase(plan.newKg)}`,
      detail: p === null ? '' : `stays ${money(p)} ${unitPhrase(plan.newKg)}, ${line(p)}` }
  ];
}

/**
 * A plan's money in words, for the save's answer and the history: "price
 * £369.00 → £184.50 a 500kg tote (it was per a tonne); cost £315.00 →
 * £157.50". '' when nothing moves.
 */
export function unitsMoneyText(plan) {
  if (!plan || plan.sameKg) return '';
  const bits = [];
  const moved = (m) => m && m.old !== m.new && !(m.old === null && m.new === null);
  if (moved(plan.price)) bits.push(`price ${costMoney(plan.price.old) || 'none'} → ${costMoney(plan.price.new) || 'none'}`);
  if (moved(plan.cost)) bits.push(`cost ${costMoney(plan.cost.old) || 'none'} → ${costMoney(plan.cost.new) || 'none'}`);
  const why = plan.basis === 'old' ? ` (it was per ${unitPhrase(plan.oldKg)}, now ${unitPhrase(plan.newKg)})`
    : plan.basis === 'new' ? ` (it was already per ${unitPhrase(plan.newKg)})`
      : plan.basis === 'typed' ? ' (typed)' : '';
  if (!bits.length) return plan.basis === 'new' ? `price and cost kept${why}` : '';
  return `${bits.join('; ')}${why}`;
}

/*
 * ── Gone out: the corrected copy for the customer (1.1.0) ───────────────────
 */

/** What the customer may be sent after a gone-out invoice was corrected. */
export const CORRECTED_SEND_MODES = ['statement', 'invoice', 'none'];

const money2 = (n) => `£${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dropPallets = (t) => String(t || '').replace(/\s*\(\d[\d.,]*\s+pallets?\)/, '');

/**
 * The note printed on the corrected invoice and the interim statement, and in
 * the email: "Corrected on 01/11/2026: SO-D10442 Supreme Rearer 18 in totes —
 * 2.00 T corrected to 2 × 500kg tote · 1.00 T; £738.00 → £369.00".
 * @param {{ day: string, orderNumber?: string, lines: Array<{ description?, item_code?, units?: { old, new } }>,
 *   oldGross: number, newGross: number }} a — day 'YYYY-MM-DD'
 */
export function correctionNoteText({ day, orderNumber = null, lines = [], oldGross, newGross } = {}) {
  const what = (lines || []).map(l => `${l.description || l.item_code || 'a line'} — ${dropPallets(l.units?.old) || 'its old units'} corrected to ${dropPallets(l.units?.new) || 'its new units'}`).join('; ');
  const totals = Math.abs((Number(oldGross) || 0) - (Number(newGross) || 0)) >= 0.005 ? `; ${money2(oldGross)} → ${money2(newGross)}` : '';
  return `Corrected on ${ukDay(day)}: ${orderNumber ? `${orderNumber} ` : ''}${what}${totals}`;
}

/** The calendar month an invoice is dated in: { start, end, key 'YYYY-MM' }. */
export function statementMonthOf(invoiceDate) {
  const m = /^(\d{4})-(\d{2})/.exec(String(invoiceDate || ''));
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return { start: `${m[1]}-${m[2]}-01`, end: `${m[1]}-${m[2]}-${String(last).padStart(2, '0')}`, key: `${m[1]}-${m[2]}` };
}

/**
 * Why "Statement and invoices" cannot be offered, or null. An interim
 * statement is a whole month that has ended (utils/statementRunKind), so an
 * invoice dated in the current month (London `today`) cannot have been on a
 * statement yet — the corrected invoice alone is the default then.
 */
export function statementSendBlock(invoiceDate, today, { approved = true } = {}) {
  const month = statementMonthOf(invoiceDate);
  if (!month) return 'The invoice has no date';
  const now = String(today || '').slice(0, 7);
  if (now && month.key >= now) return 'It is dated this month, so it has not been on a statement yet — send the corrected invoice';
  // A statement carries only invoices approved for statements: one that is
  // not was on none, and an interim statement would leave it off.
  if (!approved) return 'It has not been approved for a statement, so it was on none — send the corrected invoice';
  return null;
}
